import { Errors } from './validator'

type ParsedError = {
  Code: number
  Description: string
}

const serverErrorCodeToValidationCodeMapping = {
  InvalidAccountName: 'accountName',
  '105': 'tradingStatusClosed',
  '107': 'accountName',
  '115': 'collateral'
}

const serverErrorCodeToValidationMessage = {
  collateral: 'User cannot make changes as the note is used as collateral for a loan',
  DuplicateEmail: 'Unable to create user with these credentials',
  tradingStatusClosed: (description: string) => {
    const asset = description.slice(description.indexOf('(') + 1, description.indexOf(')'))
    return `This trade contains an asset which is currently not open to trading, and therefore the trade cannot be submitted at this time.  Please either remove the [${asset}] from the trade or wait until it becomes open for trading to re-submit.`
  }
}

const getErrorCode = (error: ParsedError) =>
  serverErrorCodeToValidationCodeMapping[error.Code] || error.Code

const parseError = (error: any): ParsedError => {
  if (typeof error === 'string') return JSON.parse(error)

  if (error.code) return { Code: error.code, Description: error.description }

  return error
}

export function extractErrorMessagesFromServerResponse(data: any): Errors {
  const errors: Errors = {}
  if (!data) {
    errors.Unknown = 'An unknown error occurred'
    return errors
  }
  // API Type : ApplicationError
  // Example validation error response:
  // [{"code":"DuplicateEmail","description":"Email 'test@test.com' is already taken."}]
  if (Array.isArray(data)) {
    data.forEach(dataError => {
      const parsedError = parseError(dataError)
      const errorCode = getErrorCode(parsedError)
      errors[errorCode] = parsedError.Description
    })
  } else if (Array.isArray(data.error)) {
    data.error.forEach(dataError => {
      const parsedError = parseError(dataError)
      const errorCode = getErrorCode(parsedError)
      if (errorCode === 'collateral') {
        errors[errorCode] = serverErrorCodeToValidationMessage[errorCode]
      } else if (errorCode === 'tradingStatusClosed') {
        errors[errorCode] = serverErrorCodeToValidationMessage[errorCode](parsedError.Description)
      } else {
        // this code doesn't appear to work
        // above section does work as it should for collateral
        // have left it like this as not sure how the UI would react if fixed
        errors[dataError.code] = dataError.description
      }
    })
  } else if (data.error?.InnerException?.Message) {
    // Example
    // InnerException: {ClassName: "System.ApplicationException", Message: "No update instructions found to submit",…}
    errors.ApplicationInnerException = data.error.InnerException.Message
  } else if (data.error?.innerException?.message) {
    // Example
    // innerException: {ClassName: "System.ApplicationException", Message: "No update instructions found to submit",…}
    errors.ApplicationInnerException = data.error.innerException.message
  } else if (data.error?.Message) {
    // Example 500 server response with exception message
    // { Message: "Some error here" }
    errors.ApplicationException = data.error.Message
  } else if (data.error?.message) {
    // Example 500 server response with exception message
    // { Message: "Some error here" }
    errors.ApplicationException = data.error.message
  }
  return errors
}

// MB: leaving this method to avoid the scope of change.
// all trade builder code is using the above method to convert all validation/server-errors to standar error object
// so we never miss to see any problem in page.
// the below method is used by older screens and not including those in current refactoring scope
export function extractErrorMessagesFromServerResponseToArray(data: any) {
  if (!data) {
    return ['An unknown error occurred']
  }

  // Example validation error response:
  // [{"code":"DuplicateEmail","description":"Email 'test@test.com' is already taken."}]
  let errorMessages = []
  if (Array.isArray(data.error)) {
    errorMessages = data.error.map(x => serverErrorCodeToValidationMessage[x.code] ?? x.description)
  } else if (data.error?.Message) {
    // Example 500 server response with exception message
    // { Message: "Some error here" }
    errorMessages = [data.error.Message]
  } else {
    errorMessages = [JSON.stringify(data.error)]
  }

  return errorMessages
}
